'use client';

/**
 * DatePicker — Phase A5 picker family v0.1 (date only).
 *
 * AU-friendly text date entry styled to match the Statura design system.
 * Emits ISO `YYYY-MM-DD` values while allowing direct `DD/MM/YYYY` typing.
 *
 * tz contract: the consumer is responsible for converting the returned
 * `value` (`YYYY-MM-DD` string in the user's browser local) into the
 * tenant timezone before persisting. The picker itself is timezone-
 * naive — it stores and returns a calendar date, not a moment in time.
 *
 * Spec: `UX_PRODUCTION_ROADMAP.md` §A5 (pickers).
 *
 * DateRangePicker, TimePicker, ClientPicker, StaffPicker, FacilityPicker,
 * RoomPicker, BedPicker — Sprint 2 v0.2 / Sprint 3.
 */

import React from 'react';
import { Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';
import { DateEntryInput } from '../date-entry-input';

export interface DatePickerProps {
  /** Field id — wires the label + the input for screen readers. */
  id?: string;
  /** Visible label rendered above the input. */
  label?: string;
  /** Optional help text rendered under the input. */
  helpText?: React.ReactNode;
  /** Validation error — replaces help text when present, in error styling. */
  error?: string;
  /**
   * Controlled value as `YYYY-MM-DD` (browser-local calendar date).
   * Omit for uncontrolled use (native form submission via `name`).
   */
  value?: string;
  /**
   * Uncontrolled initial value — applies only when `value` is omitted.
   * Use this for native-form-submission flows.
   */
  defaultValue?: string;
  /** Change handler — fires on user pick or clear. Empty string = cleared. */
  onChange?: (value: string) => void;
  /** Earliest allowed date (`YYYY-MM-DD`). */
  min?: string;
  /** Latest allowed date (`YYYY-MM-DD`). */
  max?: string;
  /** Required field — adds the asterisk to the label + aria-required. */
  required?: boolean;
  /** Disabled state. */
  disabled?: boolean;
  /** Placeholder text shown when value is empty. */
  placeholder?: string;
  /** Optional `name` for native form submission. */
  name?: string;
  className?: string;
}

export function DatePicker({
  id,
  label,
  helpText,
  error,
  value,
  defaultValue,
  onChange,
  min,
  max,
  required,
  disabled,
  placeholder,
  name,
  className,
}: DatePickerProps) {
  // Codex Phase A Sprint 2 pass 1 P2: call useId unconditionally so
  // changing the `id` prop across renders doesn't violate the Rules
  // of Hooks (`id ?? React.useId()` short-circuits and skips the
  // hook when an explicit id is supplied).
  const autoId = React.useId();
  const inputId = id ?? `date-picker-${autoId}`;
  const helpId = `${inputId}-help`;
  const [validationMessage, setValidationMessage] = React.useState('');
  const visibleMessage = error || validationMessage || helpText;
  const hasError = !!error || !!validationMessage;
  return (
    <div className={cn('flex flex-col gap-1', className)} data-date-picker>
      {label && (
        <label htmlFor={inputId} className="text-[11.5px] font-semibold text-statura-ink">
          {label}
          {required && (
            <span className="ml-0.5 text-red-600" aria-label="required">
              *
            </span>
          )}
        </label>
      )}
      <div className="relative">
        <Calendar
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted"
          aria-hidden="true"
        />
        {/*
          Codex Phase A Sprint 2 pass 2 P2: only set `value` when a
          controlled value is supplied — otherwise pass `defaultValue`
          (or neither) so the native input owns its state and native
          form submission via `name` works. Mixing controlled `value=''`
          with no `onChange` locks the input on empty + breaks
          uncontrolled flows.
         */}
        <DateEntryInput
          id={inputId}
          name={name}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          min={min}
          max={max}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          onValidationMessageChange={setValidationMessage}
          aria-required={required}
          aria-invalid={!!error || !!validationMessage}
          aria-describedby={visibleMessage ? helpId : undefined}
          className={cn(
            'h-9 w-full rounded-md border bg-white pl-8 pr-2.5 text-xs text-statura-ink placeholder:text-text-muted',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible/40',
            hasError
              ? 'border-red-300 focus:border-red-500'
              : 'border-slate-200 focus:border-statura-green-accessible',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        />
      </div>
      {visibleMessage && (
        <p
          id={helpId}
          className={cn('text-[10.5px]', hasError ? 'text-red-600' : 'text-text-muted')}
        >
          {visibleMessage}
        </p>
      )}
    </div>
  );
}
